import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { toast } from '@/components/ui/Toast';
import { useAuth } from '@/lib/auth/useAuth';
import { useT } from '@/lib/i18n/useT';
import { usePrefsStore } from '@/lib/prefs/usePrefsStore';
import { legalApi, type LegalAcceptance, type LegalDocumentType } from './api';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `legal_setup_ack` (Exhibit F p.1) o `data_export_ack` (Exhibit F p.2). */
  documentType: Extract<LegalDocumentType, 'legal_setup_ack' | 'data_export_ack'>;
  /** La marca (co_companies.id) a la que se refiere el acto. */
  brandId: number;
  /** Cómo se llama la marca / su razón social, para el texto. */
  brandLegalName: string;
  /** El nombre de la activación o del conjunto de datos, para el texto. */
  subject: string;
  /** Resumen generado por el sistema: lo que se va a reconocer, línea a línea. */
  summary: Array<{ label: string; value: string }>;
  /** La foto que se guarda con el reconocimiento (configuración o descriptor del export). */
  payload: Record<string, unknown>;
  /** Con la fila escrita, quien llama sigue (guarda, exporta). */
  onAcknowledged: (ack: LegalAcceptance) => void;
}

/**
 * Las dos páginas de reconocimiento del representante (Exhibit F).
 *
 * Dos momentos en que la responsabilidad pasa de Onxite a la marca, y cada uno
 * deja una fila inmutable en `co_legal_acceptance` con nombre, cargo, la foto
 * de lo reconocido y la hora: al guardar la configuración legal (la marca
 * responde de los instrumentos que verán los invitados) y antes de exportar
 * datos fila a fila (la marca toma la custodia de la copia). Sin la fila, el
 * backend no guarda (428) ni sirve el fichero (428): esto no es un aviso que
 * se cierra, es el paso que falta.
 *
 * Cada casilla se marca por separado —son actos distintos— y el cargo se
 * escribe a mano: el nombre viene de la cuenta, el cargo no lo sabemos.
 */
export function AcknowledgmentDialog({
  open,
  onOpenChange,
  documentType,
  brandId,
  brandLegalName,
  subject,
  summary,
  payload,
  onAcknowledged,
}: Props) {
  const t = useT();
  const { user } = useAuth();
  const language = usePrefsStore((s) => s.language);
  const nombreCuenta = [user?.firstname, user?.lastname].filter(Boolean).join(' ').trim();

  const esSetup = documentType === 'legal_setup_ack';
  const k = esSetup ? 'legalAck.setup' : 'legalAck.export';
  const casillas = esSetup ? ['authority', 'controller', 'documents'] : ['authority', 'custody'];

  const [marcadas, setMarcadas] = useState<Record<string, boolean>>({});
  const [nombre, setNombre] = useState(nombreCuenta);
  const [cargo, setCargo] = useState('');
  const todas = casillas.every((c) => marcadas[c]);
  const puede = todas && nombre.trim().length > 1 && cargo.trim().length > 1;

  const firmar = useMutation({
    mutationFn: () =>
      legalApi.record({
        documentType,
        brandId,
        signerName: nombre.trim(),
        signerTitle: cargo.trim(),
        locale: language === 'es' ? 'es' : 'en',
        payload: { ...payload, signerName: nombre.trim(), signerTitle: cargo.trim(), subject },
      }),
    onSuccess: (ack) => {
      setMarcadas({});
      onAcknowledged(ack);
    },
    onError: (e: unknown) =>
      toast({ tone: 'error', title: t('legalAck.error'), description: e instanceof Error ? e.message : undefined }),
  });

  const vars = { brand: brandLegalName, subject };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-[var(--color-accent-ink)]" />
              {t(`${k}.title`)}
            </span>
          </DialogTitle>
          <DialogDescription>{t(`${k}.intro`, vars)}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] p-3">
            <p className="text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-fg-muted)]">
              {t('legalAck.summary')}
            </p>
            <dl className="mt-2 grid gap-1 text-xs">
              {summary.map((s) => (
                <div key={s.label} className="flex justify-between gap-3">
                  <dt className="text-[var(--color-fg-muted)]">{s.label}</dt>
                  <dd className="text-right font-medium text-[var(--color-fg-primary)]">{s.value}</dd>
                </div>
              ))}
            </dl>
          </div>

          {!esSetup ? (
            <ul className="list-disc space-y-1 pl-5 text-xs leading-relaxed text-[var(--color-fg-muted)]">
              <li>{t('legalAck.export.point.security', vars)}</li>
              <li>{t('legalAck.export.point.rights', vars)}</li>
              <li>{t('legalAck.export.point.marketing')}</li>
              <li>{t('legalAck.export.point.logged', vars)}</li>
            </ul>
          ) : null}

          <div className="space-y-2">
            {casillas.map((c) => (
              <label key={c} className="flex cursor-pointer items-start gap-2 text-sm leading-snug text-[var(--color-fg-primary)]">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 accent-[var(--color-accent)]"
                  checked={!!marcadas[c]}
                  onChange={(e) => setMarcadas((m) => ({ ...m, [c]: e.target.checked }))}
                />
                <span>{t(`${k}.check.${c}`, vars)}</span>
              </label>
            ))}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-fg-muted)]">
                {t('legalAck.name')}
              </span>
              <Input value={nombre} onChange={(e) => setNombre(e.target.value)} className="mt-1" />
            </label>
            <label className="block">
              <span className="text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-fg-muted)]">
                {t('legalAck.title')}
              </span>
              <Input
                value={cargo}
                onChange={(e) => setCargo(e.target.value)}
                placeholder={t('legalAck.titlePlaceholder')}
                className="mt-1"
              />
            </label>
          </div>
          <p className="text-[11px] leading-snug text-[var(--color-fg-subtle)]">{t('legalAck.recordNote')}</p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={firmar.isPending}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => firmar.mutate()} disabled={!puede} loading={firmar.isPending}>
            {t(`${k}.confirm`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
